import {
    Fade,
    Menu,
    type MenuProps,
    type PopoverProps,
    type Theme,
    useTheme,
} from '@mui/material';
import type { KeyboardEvent, ReactNode } from 'react';

/** Gap between a menu and a flyout that opens beside it. */
const FLYOUT_GAP_PX = 4;

/**
 * The menu paper's border and padding above its first row, from the theme's
 * Menu: with it taken off, the flyout's first row lines up with `anchorEl`.
 */
const PAPER_INSET_PX = 1 + 4;

/**
 * Where a flyout opens from `anchorEl`, a row of a menu: one gap right of
 * that menu's paper, its first row level with the row; undefined without a
 * row.
 */
function flyoutAnchorPosition(
    anchorEl: HTMLElement | null
): { left: number; top: number } | undefined {
    const parentPaper = anchorEl?.closest('.MuiPaper-root') ?? anchorEl;
    return anchorEl && parentPaper
        ? {
              left: parentPaper.getBoundingClientRect().right + FLYOUT_GAP_PX,
              top: anchorEl.getBoundingClientRect().top - PAPER_INSET_PX,
          }
        : undefined;
}

/**
 * The popover props a flyout beside the menu holding `anchorEl` shares with
 * every other: placed by `flyoutAnchorPosition`, closed while it has no
 * place, fading in, with no backdrop of its own, letting the pointer through
 * outside its paper, and closed by Left Arrow, which goes no further.
 */
export function flyoutPopoverProps(
    anchorEl: HTMLElement | null,
    open: boolean,
    onClose: () => void,
    theme: Theme
) {
    const anchorPosition = flyoutAnchorPosition(anchorEl);
    return {
        open: open && anchorPosition !== undefined,
        onClose,
        anchorReference: 'anchorPosition',
        anchorPosition,
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        TransitionComponent: Fade,
        transitionDuration: theme.transitions.duration.short,
        hideBackdrop: true,
        onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopPropagation();
                onClose();
            }
        },
        sx: { pointerEvents: 'none' },
    } satisfies Partial<PopoverProps>;
}

/**
 * A menu that opens beside the menu holding `anchorEl`, one of its rows:
 * one gap right of that menu's paper, its first row level with the row.
 * The pointer passes through to the menu under it, so a click on another
 * of its rows lands there; a click outside both reaches that menu's
 * backdrop, which closes it. Escape and Left Arrow close only the flyout,
 * and focus goes back to the row it opened from.
 */
export default function MenuFlyout({
    anchorEl,
    open,
    onClose,
    width,
    MenuListProps,
    children,
    'data-testid': testId,
}: {
    /** The menu row the flyout opens from. */
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    width?: number;
    MenuListProps?: MenuProps['MenuListProps'];
    children: ReactNode;
    'data-testid'?: string;
}) {
    const theme = useTheme();
    return (
        <Menu
            {...flyoutPopoverProps(anchorEl, open, onClose, theme)}
            disableEnforceFocus
            MenuListProps={MenuListProps}
            data-testid={testId}
            slotProps={{
                paper: {
                    sx: {
                        pointerEvents: 'auto',
                        mt: 0,
                        ...(width !== undefined && { width, maxWidth: 'none' }),
                    },
                },
            }}
        >
            {children}
        </Menu>
    );
}
